import { useGoogleReCaptcha } from '@wojtekmaj/react-recaptcha-v3';

let cachedToken: string | null = null;
let tokenTimestamp: number = 0;
const TOKEN_EXPIRY_MS = 2 * 60 * 1000; // Tokens expire after 2 minutes

/**
 * Get the current reCAPTCHA token synchronously from cache.
 * Returns null if no token has been cached yet or if expired.
 */
export const getRecaptchaTokenSync = (): string | null => {
  const now = Date.now();
  if (cachedToken && now - tokenTimestamp < TOKEN_EXPIRY_MS) {
    return cachedToken;
  }
  cachedToken = null;
  return null;
};

/**
 * Update the cached token (called from React component context).
 */
export const setRecaptchaToken = (token: string): void => {
  cachedToken = token;
  tokenTimestamp = Date.now();
};

/**
 * Hook to capture and cache the reCAPTCHA token.
 * Use this inside a component wrapped by GoogleReCaptchaProvider.
 */
export const useRecaptchaToken = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Refresh token periodically or on demand
  const refreshToken = async (action: string = 'submit'): Promise<string> => {
    if (!executeRecaptcha) {
      console.warn('reCAPTCHA not yet available');
      return '';
    }

    try {
      const token = await executeRecaptcha(action);
      if (token) {
        setRecaptchaToken(token);
      }
      return token ?? '';
    } catch (error) {
      console.error('Failed to execute reCAPTCHA:', error);
      return '';
    }
  };

  return { refreshToken };
};
